/** @jsxImportSource @emotion/react */
// filepath: /Users/a1111/WebstormProjects/arch-editor/src/components/whiteboard/WhiteboardContextMenu.tsx
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { css } from '@emotion/react';

export interface ContextMenuItem {
  label: string;
  action?: () => void;
  shortcut?: string;
  disabled?: boolean;
  dangerous?: boolean;
  submenu?: ContextMenuItem[];
  className?: string;
  // Позволяет рендерить кастомный контент вместо стандартного текста
  render?: (item: ContextMenuItem, closeMenu: () => void) => React.ReactNode;
}

interface WhiteboardContextMenuProps {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}

const menuBaseStyle = css`
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  min-width: 180px;
  padding: 4px 0;
  z-index: 1000;
  animation: fadeIn 0.15s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const menuFixedStyle = css`
  position: fixed;
  ${menuBaseStyle}
`;

const menuItemStyle = (disabled: boolean, danger: boolean) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  cursor: ${disabled ? 'not-allowed' : 'pointer'};
  color: ${disabled ? '#bfbfbf' : danger ? '#ff4d4f' : '#333'};
  font-size: 14px;
  transition: background-color 0.15s;

  &:hover {
    background-color: ${disabled ? 'transparent' : danger ? '#fff1f0' : '#f5f5f5'};
  }
`;

const shortcutStyle = css`
  color: #999;
  font-size: 12px;
  margin-left: 24px;
`;

const dividerStyle = css`
  height: 1px;
  background: #f0f0f0;
  margin: 4px 0;
`;

const iconStyle = css`
  margin-right: 8px;
  display: flex;
  align-items: center;
`;

export const WhiteboardContextMenu: React.FC<WhiteboardContextMenuProps> = memo(({
  x,
  y,
  items,
  onClose,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Закрытие при клике вне меню
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    // Используем setTimeout чтобы не закрыть сразу при открытии
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Корректировка позиции если меню выходит за границы экрана
  useEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let adjustedX = x;
      let adjustedY = y;

      if (x + rect.width > viewportWidth) {
        adjustedX = viewportWidth - rect.width - 8;
      }
      if (y + rect.height > viewportHeight) {
        adjustedY = viewportHeight - rect.height - 8;
      }

      menuRef.current.style.left = `${adjustedX}px`;
      menuRef.current.style.top = `${adjustedY}px`;
    }
  }, [x, y]);

  const handleItemClick = useCallback((item: ContextMenuItem) => {
    if (item.disabled || !item.action || item.submenu) return;
    item.action();
    onClose();
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      css={menuFixedStyle}
      style={{ left: x, top: y }}
    >
      {items.map((item, index) => {
        // Handle divider
        if (item.label === 'divider') {
          return <div key={`divider-${index}`} css={dividerStyle} />;
        }

        // Handle custom render
        if (item.render) {
            return (
                <div key={index} onClick={(e) => e.stopPropagation()}>
                    {item.render(item, onClose)}
                </div>
            );
        }

        // Handle submenu
        if (item.submenu) {
          return (
            <div key={index} style={{ position: 'relative' }}>
              <div
                css={menuItemStyle(!!item.disabled, !!item.dangerous)}
                style={{ justifyContent: 'space-between' }}
              >
                <span>{item.label}</span>
                <span style={{ marginLeft: 16 }}>▶</span>
              </div>
              <div
                css={css`
                  ${menuBaseStyle}
                  position: absolute;
                  left: 100%;
                  top: 0;
                  display: none;
                  
                  div:hover > & {
                    display: block;
                  }
                `}
              >
                {item.submenu.map((subItem, subIndex) => (
                  <div
                    key={subIndex}
                    css={menuItemStyle(!!subItem.disabled, !!subItem.dangerous)}
                    onClick={() => {
                      if (!subItem.disabled && subItem.action) {
                        subItem.action();
                        onClose();
                      }
                    }}
                  >
                    <span>{subItem.label}</span>
                    {subItem.shortcut && <span css={shortcutStyle}>{subItem.shortcut}</span>}
                  </div>
                ))}
              </div>
            </div>
          );
        }

        // Regular menu item
        return (
          <div
            key={index}
            css={menuItemStyle(!!item.disabled, !!item.dangerous)}
            onClick={() => handleItemClick(item)}
          >
            <span>{item.label}</span>
            {item.shortcut && <span css={shortcutStyle}>{item.shortcut}</span>}
          </div>
        );
      })}
    </div>
  );
});

WhiteboardContextMenu.displayName = 'WhiteboardContextMenu';

export default WhiteboardContextMenu;

